/**
 * Seeded RNG (mulberry32). The whole generator state is one uint32 kept in
 * GameState.rngState, so states stay JSON-serializable and replayable.
 * All functions are pure: they take a state and return the next one.
 */

/** Normalize any number to a uint32 seed. */
export function toUint32(n: number): number {
  return (Math.trunc(n) >>> 0) as number;
}

/** One mulberry32 step: returns a float in [0, 1) and the next state. */
export function nextFloat(state: number): [number, number] {
  const next = (state + 0x6d2b79f5) >>> 0;
  let t = next;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  const value = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  return [value, next];
}

/** Integer in [0, n). */
export function nextInt(state: number, n: number): [number, number] {
  const [f, s] = nextFloat(state);
  return [Math.floor(f * n), s];
}

/** Fisher-Yates shuffle into a new array. */
export function shuffle<T>(items: readonly T[], state: number): [T[], number] {
  const out = items.slice();
  let s = state;
  for (let i = out.length - 1; i > 0; i--) {
    let j: number;
    [j, s] = nextInt(s, i + 1);
    const tmp = out[i] as T;
    out[i] = out[j] as T;
    out[j] = tmp;
  }
  return [out, s];
}

/** Mix several numbers into one uint32 (for deriving independent seeds). */
export function mixSeed(...parts: number[]): number {
  let h = 0x811c9dc5;
  for (const p of parts) {
    h = Math.imul(h ^ toUint32(p), 0x01000193) >>> 0;
    h ^= h >>> 13;
    h = Math.imul(h, 0x5bd1e995) >>> 0;
    h ^= h >>> 15;
  }
  return h >>> 0;
}

/** A small mutable wrapper for code that draws many numbers (bots). */
export class Rng {
  constructor(public state: number) {
    this.state = toUint32(state);
  }
  float(): number {
    const [f, s] = nextFloat(this.state);
    this.state = s;
    return f;
  }
  int(n: number): number {
    return Math.floor(this.float() * n);
  }
  pick<T>(items: readonly T[]): T {
    if (items.length === 0) throw new Error('pick from empty list');
    return items[this.int(items.length)] as T;
  }
  chance(p: number): boolean {
    return this.float() < p;
  }
}
